import cn from "../../lib/classnames";
import React from "react";
import styles from "./ActionButton.module.css";
import {Link} from "react-router-dom";
import {Location} from "history";

export enum ActionButtonStyle {
    primary,
    secondary,
    outlined,
    outlinedDark,
    outlinedSmall,
}

type Props<T = unknown> = {
    className?: string;
    labelClassName?: string;
    label: string;
    description?: string;
    onClick?: () => void;
    href?: string;
    linkTo?: string | Location<T>;
    style?: ActionButtonStyle;
    fullWidth?: boolean;
    openInSameTab?: boolean;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    type?: "submit" | "button";
    loading?: boolean;
    disabled?: boolean;
    search?: string;
};

function styleClassnames(style: ActionButtonStyle) {
    switch (style) {
        case ActionButtonStyle.primary:
            return styles.primary;
        case ActionButtonStyle.secondary:
            return styles.secondary;
        case ActionButtonStyle.outlined:
            return styles.outlined;
        case ActionButtonStyle.outlinedDark:
            return styles.outlinedDark;
        case ActionButtonStyle.outlinedSmall:
            return styles.outlinedSmall;
    }
}

const ActionButton = React.forwardRef<HTMLButtonElement, Props>(
    (
        {
            className,
            labelClassName,
            label,
            description,
            style = ActionButtonStyle.primary,
            fullWidth = false,
            onClick,
            href,
            linkTo,
            openInSameTab = false,
            onKeyDown,
            type,
            loading,
            disabled,
            search,
        }: Props,
        ref,
    ) => {
        const labelElement = (
            <div
                className={cn(
                    styleClassnames(style),
                    styles.label,
                    description ? styles.withDescription : undefined,
                    loading ? styles.hideText : undefined,
                    labelClassName,
                )}
            >
                {label}
            </div>
        );

        const descriptionElement = description ? (
            <div
                className={cn(
                    styleClassnames(style),
                    styles.description,
                    loading ? styles.hideText : undefined,
                )}
            >
                {description}
            </div>
        ) : null;

        const buttonStyles = cn(
            className,
            styleClassnames(style),
            styles.button,
            fullWidth ? styles.fullWidth : undefined,
            description ? styles.withDescription : undefined,
            linkTo || href ? styles.asAnchor : undefined,
        );
        if (linkTo) {
            let to = linkTo;
            const pathname = typeof to === "string" ? to : "";
            if (search && pathname) {
                to = {pathname, search, state: undefined, hash: ""};
            }
            return (
                <Link
                    to={to}
                    onClick={onClick}
                    aria-label={label}
                    className={buttonStyles}
                    target={openInSameTab ? undefined : "_blank"}
                    rel={openInSameTab ? undefined : "noopener noreferrer"}
                    onKeyDown={onKeyDown}
                >
                    <div className={cn(styles.loader, loading ? styles.showLoader : undefined)} />
                    {labelElement}
                    {descriptionElement}
                </Link>
            );
        } else if (href) {
            return (
                <a
                    href={href}
                    onClick={onClick}
                    aria-label={label}
                    className={buttonStyles}
                    target={openInSameTab ? undefined : "_blank"}
                    rel={openInSameTab ? undefined : "noopener noreferrer"}
                    onKeyDown={onKeyDown}
                >
                    <div className={cn(styles.loader, loading ? styles.showLoader : undefined)} />
                    {labelElement}
                    {descriptionElement}
                </a>
            );
        }
        return (
            <>
                <button
                    ref={ref}
                    onClick={onClick}
                    aria-label={label}
                    className={buttonStyles}
                    onKeyDown={onKeyDown}
                    type={type}
                    disabled={loading || disabled}
                >
                    <div className={cn(styles.loader, loading ? styles.showLoader : undefined)} />
                    {labelElement}
                    {descriptionElement}
                </button>
            </>
        );
    },
);

export default ActionButton;
